import { takeStalePageRetrySlot } from "../ui/stalePageReload";

const releaseCheckIntervalMs = 5 * 60 * 1000;

function entryAssetFromDocument(root: Document, baseUrl: string): string | null {
  const source = root.querySelector<HTMLScriptElement>('script[type="module"][src]')?.getAttribute("src");
  if (!source) return null;
  try {
    return new URL(source, baseUrl).pathname;
  } catch {
    return null;
  }
}

async function latestEntryAsset(): Promise<string | null> {
  const url = new URL("/", window.location.origin);
  url.searchParams.set("release-check", Date.now().toString());
  const response = await fetch(url, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" },
  });
  if (!response.ok) return null;
  const html = await response.text();
  const document = new DOMParser().parseFromString(html, "text/html");
  return entryAssetFromDocument(document, window.location.origin);
}

export function initializeReleaseFreshnessChecks(): () => void {
  if (!import.meta.env.PROD) return () => undefined;

  const loadedEntryAsset = entryAssetFromDocument(document, window.location.href);
  if (!loadedEntryAsset) return () => undefined;

  let stopped = false;
  let checking = false;
  const check = async () => {
    if (stopped || checking || !navigator.onLine) return;
    checking = true;
    try {
      const currentEntryAsset = await latestEntryAsset();
      if (currentEntryAsset && currentEntryAsset !== loadedEntryAsset && takeStalePageRetrySlot()) {
        window.location.reload();
      }
    } catch {
      // A release check should never interrupt the current page when the network is unavailable.
    } finally {
      checking = false;
    }
  };

  const checkWhenVisible = () => {
    if (document.visibilityState === "visible") void check();
  };
  window.addEventListener("focus", checkWhenVisible);
  window.addEventListener("online", checkWhenVisible);
  document.addEventListener("visibilitychange", checkWhenVisible);
  const initialTimer = window.setTimeout(() => void check(), 1_000);
  const interval = window.setInterval(() => void check(), releaseCheckIntervalMs);

  return () => {
    stopped = true;
    window.clearTimeout(initialTimer);
    window.clearInterval(interval);
    window.removeEventListener("focus", checkWhenVisible);
    window.removeEventListener("online", checkWhenVisible);
    document.removeEventListener("visibilitychange", checkWhenVisible);
  };
}
